import {
  Button,
  Center,
  Drawer,
  Loader,
  Stack,
  Text,
  Textarea,
} from "@mantine/core";
import React, { FC, useEffect, useState } from "react";
import { Resume } from "../models/Resume";
import { fetchWithAuthentication } from "../utilities/FetchUtil";
import { useForm } from "@mantine/form";
import { convertToLocalDateTime } from "../utilities/DateUtil";
import {
  AiTaskStatus,
  ResumeSummarizeTaskDTO,
  ResumeSummarizeTaskStatus,
} from "../models/Message";

interface ResumeDrawerProps {
  drawerOpened: boolean;
  drawerHandlers: {
    readonly open: () => void;
    readonly close: () => void;
    readonly toggle: () => void;
  };
  chatSessionId: string | undefined;
  resumeSummarizeTaskStatusDto: ResumeSummarizeTaskDTO | null;
  setResumeSummarizeTaskStatusDto: (statusDto: ResumeSummarizeTaskDTO) => void;
}

const ResumeDrawer: FC<ResumeDrawerProps> = ({
  drawerOpened,
  drawerHandlers,
  chatSessionId,
  resumeSummarizeTaskStatusDto,
  setResumeSummarizeTaskStatusDto,
}) => {
  const [resume, setResume] = useState<Resume | null>(null);
  const [taskStatus, setTaskStatus] =
    useState<ResumeSummarizeTaskStatus | null>(null);

  const resumeForm = useForm({
    mode: "uncontrolled",
    initialValues: {
      text: "",
    },
    validate: {
      text: (text: string) =>
        text.trim().length < 100
          ? "Resume must be longer than 100 characters."
          : text.trim().length >= 8000
            ? "Resume must be fewer than 8,000 characters."
            : null,
    },
  });

  const getResume = async () => {
    const response = await fetchWithAuthentication(
      `/resume/${chatSessionId}`,
      "GET",
      null,
    );
    if (response.ok) {
      const json = await response.json();
      setResume(json.resumeData);
      setTaskStatus(json.taskStatus);
    } else {
      setTaskStatus(ResumeSummarizeTaskStatus.FAILED);
    }
  };

  const postResume = async () => {
    const data = {
      chatSessionId: chatSessionId,
      resumeText: resumeForm.getValues().text,
    };
    resumeForm.reset();
    const response = await fetchWithAuthentication("/resume", "POST", data);
    if (response.ok) {
      const json = await response.json();
      setResumeSummarizeTaskStatusDto({
        status: json.status,
        resumeSummarizeTaskId: json.aiTaskId,
      });
      setTaskStatus(ResumeSummarizeTaskStatus.PROCESSING);
    } else {
      setTaskStatus(ResumeSummarizeTaskStatus.FAILED);
    }
  };

  useEffect(() => {
    if (resumeSummarizeTaskStatusDto?.status === AiTaskStatus.DONE) {
      setTaskStatus(ResumeSummarizeTaskStatus.COMPLETE);
      getResume();
    }
    if (resumeSummarizeTaskStatusDto?.status === AiTaskStatus.OOPS) {
      setTaskStatus(ResumeSummarizeTaskStatus.FAILED);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeSummarizeTaskStatusDto]);

  useEffect(() => {
    if (drawerOpened) {
      getResume();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerOpened]);

  useEffect(() => {
    setResume(null);
    setTaskStatus(null);
  }, [chatSessionId]);

  return (
    <Drawer
      opened={drawerOpened}
      onClose={drawerHandlers.close}
      title="Resume management"
    >
      {!resume && taskStatus === ResumeSummarizeTaskStatus.NO_RESUME && (
        <Stack>
          <Text>
            No resume associated with this chat session. Use the area below to
            paste resume text to.
          </Text>
          <form onSubmit={resumeForm.onSubmit(postResume)}>
            <Stack>
              <Textarea
                rows={5}
                required
                {...resumeForm.getInputProps("text")}
                key={resumeForm.key("text")}
              />
              <Button type="submit">Submit</Button>
            </Stack>
          </form>
        </Stack>
      )}
      {resume && taskStatus === ResumeSummarizeTaskStatus.COMPLETE && (
        <Stack>
          <Text>
            <Text span fw={600}>
              Created at:
            </Text>{" "}
            {convertToLocalDateTime(resume.createdAt)}
          </Text>
          <Text fw={600}>Summarized resume:</Text>
          <Text>{resume.resumeSummary}</Text>
          <Text fw={600}>Full resume:</Text>
          <Text>{resume.resumeContent}</Text>
          <Text fw={600}>Update resume:</Text>
          <Text>
            To update this user's resume, paste the resume text into the field
            below and click Update.
          </Text>
          <form onSubmit={resumeForm.onSubmit(postResume)}>
            <Stack>
              <Textarea
                rows={5}
                required
                {...resumeForm.getInputProps("text")}
                key={resumeForm.key("text")}
              />
              <Button type="submit">Update</Button>
            </Stack>
          </form>
        </Stack>
      )}

      {taskStatus === ResumeSummarizeTaskStatus.PROCESSING && (
        <Center>
          <Stack align="center" gap="xs">
            <Loader size="sm" />
            <Text size="sm" c="dimmed">
              Loading resume summary...
            </Text>
          </Stack>
        </Center>
      )}

      {taskStatus === ResumeSummarizeTaskStatus.FAILED && (
        <Stack>
          <Text>Something went wrong, please try again.</Text>
          <Button onClick={getResume}>Retry</Button>
        </Stack>
      )}
    </Drawer>
  );
};

export default ResumeDrawer;
